import type { MutableRefObject } from 'react';
import type { Edge, Node, ReactFlowInstance } from '@xyflow/react';
import type { FlowchartEdgeData, FlowchartNodeData } from './flowchart-types';
import type { LayoutResult } from './layout-flowchart';

const FIT_VIEW_PADDING = 0.32;
const FIT_VIEW_DURATION = 460;
const FOCUS_START_DELAY_MS = 520;

type FlowchartInstance = ReactFlowInstance<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>>;

interface FlowchartEntrance {
  contentKey: string;
  layout: LayoutResult;
  reactFlowInstance: FlowchartInstance;
}

interface FlowchartEntranceDependencies {
  layout: LayoutResult;
  reactFlowInstance: FlowchartInstance;
}

function getFlowchartEntranceDependencies(input: {
  hasContainerSize: boolean;
  layout: LayoutResult | null;
  reactFlowInstance: FlowchartInstance | null;
}): FlowchartEntranceDependencies | null {
  if (!input.hasContainerSize) {
    return null;
  }

  if (!input.layout) {
    return null;
  }

  if (!input.reactFlowInstance) {
    return null;
  }

  return {
    layout: input.layout,
    reactFlowInstance: input.reactFlowInstance,
  };
}

export function getFlowchartEntrance(input: {
  appearedContentKey: string | null;
  contentKey: string | null;
  hasContainerSize: boolean;
  layout: LayoutResult | null;
  reactFlowInstance: FlowchartInstance | null;
}): FlowchartEntrance | null {
  const dependencies = getFlowchartEntranceDependencies(input);
  if (!dependencies) {
    return null;
  }

  if (dependencies.layout.nodes.length === 0) {
    return null;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  if (input.appearedContentKey === input.contentKey) {
    return null;
  }

  return {
    contentKey: input.contentKey ?? dependencies.layout.contentKey,
    layout: dependencies.layout,
    reactFlowInstance: dependencies.reactFlowInstance,
  };
}

export function scheduleFlowchartEntrance(input: {
  centerCameraOnNode: (nodeId: string) => void;
  focusedNodeIdRef: MutableRefObject<string | null>;
  layout: LayoutResult;
  reactFlowInstance: FlowchartInstance;
  setFocusedNodeId: (nodeId: string) => void;
}): () => void {
  let focusTimer: number | null = null;
  const animationFrame = window.requestAnimationFrame(() => {
    input.reactFlowInstance.fitView({ padding: FIT_VIEW_PADDING, duration: FIT_VIEW_DURATION });
    focusTimer = window.setTimeout(() => {
      focusStartNode(input);
    }, FOCUS_START_DELAY_MS);
  });

  return () => {
    window.cancelAnimationFrame(animationFrame);
    if (focusTimer != null) {
      window.clearTimeout(focusTimer);
    }
  };
}

function focusStartNode(input: {
  centerCameraOnNode: (nodeId: string) => void;
  focusedNodeIdRef: MutableRefObject<string | null>;
  layout: LayoutResult;
  setFocusedNodeId: (nodeId: string) => void;
}): void {
  const startNodeId = input.layout.startNodeId;
  if (!startNodeId) {
    return;
  }

  // Sync the ref before the async focus state flushes, so a settle-resize
  // landing in the same frame re-centers the start node, not fit-all.
  input.focusedNodeIdRef.current = startNodeId;
  input.setFocusedNodeId(startNodeId);
  input.centerCameraOnNode(startNodeId);
}
